import { Directive, ElementRef, forwardRef, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DrI18nService, DrLocales } from '@app/core/services/i18n/i18n.service';
import { CurrencyService } from '@app/core/services/currency.service';

const CURRENCY_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CurrencyDirective),
  multi: true
};

@Directive({
  selector: '[currency]',
  host: {
    '(input)': 'onInput($event.target.value)',
    '(blur)': 'onBlur()'
  },
  providers: [CURRENCY_VALUE_ACCESSOR]
})
export class CurrencyDirective implements ControlValueAccessor {

  protected validChars = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
  protected readonly allowedDecimalCount = 2;
  protected decimalSeparator: string;
  protected backendDecimalSeparator: string;
  protected thousandsSeparator: string;
  protected masked: Masked;

  constructor(protected renderer: Renderer2, protected elementRef: ElementRef, protected currencyService: CurrencyService) {
    this.thousandsSeparator = this.currencyService.activeCurrency.thousands_separator;
    this.decimalSeparator = this.currencyService.activeCurrency.decimal_separator;
    this.backendDecimalSeparator = DrI18nService.getLocale(DrLocales.EN).currencyFormats.decimal_separator;
    this.validChars.push(this.decimalSeparator);
  }

  public onChange = (_: any) => {};

  public onTouched = () => {};

  public registerOnChange(fn: (value: any) => any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => any): void {
    this.onTouched = fn;
  }

  public onBlur() {
    this.onInput(this.masked.masked, true);
    this.onTouched();
  }

  public onInput(value: string, addDecimals?: boolean) {
    const formatted: Masked = this.formatValue(value, addDecimals);
    this.renderer.setProperty(this.elementRef.nativeElement, 'value', formatted.masked);
    this.onChange(formatted.clean);
  }

  public writeValue(value: any): void {
    const formatted: Masked = this.formatValue(value, true);
    this.renderer.setProperty(this.elementRef.nativeElement, 'value', formatted.masked);
  }

  protected formatValue(value: any, addDecimals?: boolean): Masked {
    const empty = { clean: null, masked: '' };
    this.masked = value ?
          (typeof value === 'string' ? this.validateChars(value, addDecimals) :
          (typeof value === 'number' ? this.validateChars(this.numberToString(value), addDecimals) : empty)) :
           empty;
    return this.masked;
  }

  protected validateChars(input: string, addDecimals?: boolean): Masked {
    const inputValue = input.split('');
    this.cleanOfInvalidChars(inputValue);
    const decimalInfo = this.cleanDecimalNumbers(inputValue);

    // If 'addDecimals' is set to true this probably means the field is being blurred. We always should show 2 digits behind the decimal separator,
    // so we add them if needed
    if (addDecimals) {
      if (!decimalInfo.decimalIndex) {
        inputValue.push(this.decimalSeparator);
        decimalInfo.decimalIndex = inputValue.length - 1;
      }

      for (let i = decimalInfo.decimalCount; i < this.allowedDecimalCount; i++) {
        inputValue.push('0');
      }
    }

    // Convert the cleaned array into a number to be sent to the backend if needed
    const cleanedValueAsNumber = this.stringToNumber(inputValue.join(''));

    let counter = 0;

    // Loop from decimal separator position to the front, and add thousand separators every 3 digits
    for (let idx = decimalInfo.decimalIndex || inputValue.length; idx > 0; idx--, counter++) {
      if (counter === 3) {
        inputValue.splice(idx, 0, this.thousandsSeparator);
        counter = 0;
      }
    }

    // Convert the masked array into a string to display in the input element
    const maskedValueAsString = inputValue.join('');

    return { clean: cleanedValueAsNumber, masked: maskedValueAsString };
  }

  private cleanOfInvalidChars(values: string[]): void {
    let decimalSeparatorCount = 0;
    for (let i = (values.length - 1); i > -1; i--) {
      const val = values[i];
      const isDecimalSeparator = val === this.decimalSeparator;

      if (isDecimalSeparator) {
        decimalSeparatorCount++;
      }

      if (this.validChars.indexOf(val) === -1 || isDecimalSeparator && decimalSeparatorCount > 1) {
        values.splice(i, 1);
      }
    }
  }

  private cleanDecimalNumbers(values: string[]): DecimalInfo {
    const info = {
      decimalIndex: null,
      decimalCount: null
    };

    info.decimalIndex = values.indexOf(this.decimalSeparator);

    const maxIndex = info.decimalIndex + this.allowedDecimalCount;
    const lastIndex = values.length - 1;

    if (info.decimalIndex === 0) {
      // If the decimal separator is the first one, we simply remove it
      values.splice(info.decimalIndex, 1);
    } else if (info.decimalIndex > 0 && lastIndex > maxIndex) {
      // We only allow 2 digits after the decimal separator
      const amountToRemove = lastIndex - maxIndex;
      const startToDeleteMeIndex = maxIndex + 1;
      values.splice(startToDeleteMeIndex, amountToRemove);
    } else if (info.decimalIndex === -1) {
      info.decimalIndex = null;
    }

    info.decimalCount = info.decimalIndex ? ((values.length - 1) - info.decimalIndex) : 0;

    return info;
  }

  protected numberToString(asNumber: number): string {
    let asString = asNumber.toString();

    if (this.decimalSeparator === DrI18nService.getLocale(DrLocales.DE).currencyFormats.decimal_separator) {
      const idx = asString.indexOf(this.backendDecimalSeparator);

      if (idx > -1) {
        asString = asString.replace(this.backendDecimalSeparator, this.decimalSeparator);
      }
    }

    return asString;
  }

  protected stringToNumber(asString: string): number {
    if (this.decimalSeparator === DrI18nService.getLocale(DrLocales.DE).currencyFormats.decimal_separator) {
      const idx = asString.indexOf(this.decimalSeparator);

      if (idx > -1) {
        asString = asString.replace(this.decimalSeparator, this.backendDecimalSeparator);
      }
    }

    const asNumber = Number(asString);
    return asNumber;
  }
}

export interface Masked {
  clean: number;
  masked: string;
}

export interface DecimalInfo {
  decimalCount: number;
  decimalIndex: number;
}
